import { useCallback, useEffect, useState } from "react";
import {
  getStatusFromCookie,
  setStatusToCookie
} from "components/PageModals/utils";
import { useExitIntent } from "use-exit-intent";
import { isApproachGuidesVersion } from "src/utils/approach-guides";
import { useLocation } from "@gatsbyjs/reach-router";

type ExitIntentModalFunctions = {
  isModalOpen: boolean;
  onRequestClose: () => void;
  onFormSubmitted: () => void;
};

function useExitIntentModal(cookieName: string): ExitIntentModalFunctions {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  const { registerHandler, isTriggered } = useExitIntent({
    desktop: {
      triggerOnMouseLeave: true,
      mouseLeaveDelayInSeconds: 1
    },
    mobile: {
      triggerOnIdle: true,
      delayInSecondsToTrigger: 5
    }
  });

  const onRequestClose = useCallback(() => {
    const currentStatus = getStatusFromCookie(cookieName);
    if (currentStatus !== "submitted") {
      setStatusToCookie("closed", 60 * 60 * 24 * 15 /* 15 days */, cookieName);
    }
    setIsModalOpen(false);
  }, [cookieName]);

  const onFormSubmitted = useCallback(() => {
    setStatusToCookie("submitted", 60 * 60 * 24 * 365 /* 1 year */, cookieName);
  }, [cookieName]);

  const cookie = getStatusFromCookie(cookieName);
  const isClosed = cookie === "closed" || cookie === "submitted";

  const handleOpenModal = useCallback(() => {
    const skipOpen = isApproachGuidesVersion(location.search);
    if (canTrigger(cookieName, skipOpen)) {
      setIsModalOpen(true);
    }
  }, [cookieName, location.search]);

  useEffect(() => {
    const logo = document.querySelector<HTMLAnchorElement>(
      "[data-content-piece='logo']"
    );
    if (!logo) return;

    const logoClick = (e: MouseEvent) => {
      const skipOpen = isApproachGuidesVersion(location.search);
      if (canTrigger(cookieName, skipOpen)) {
        e.preventDefault();
        handleOpenModal();
      }
    };

    logo.addEventListener("click", logoClick);

    return () => {
      logo.removeEventListener("click", logoClick);
    };
  }, [cookieName, handleOpenModal, isTriggered, location.search]);

  if (!isClosed) {
    registerHandler({
      id: `openModal-${cookieName}`,
      handler: handleOpenModal
    });
  }

  return {
    isModalOpen,
    onRequestClose,
    onFormSubmitted
  };
}

export default useExitIntentModal;

const canTrigger = (cookieName: string, skipOpen: boolean) => {
  if (skipOpen) return false;
  const cookie = getStatusFromCookie(cookieName);
  return cookie !== "closed" && cookie !== "submitted";
};
